/**
 * Subscription entitlement refresh (T-174-002, US-174 / F-202).
 *
 * After a successful FastSpring payment the FE must unlock the purchased tier
 * immediately instead of waiting on a manual reload. This module re-reads the
 * LP subscription contract (GET /api/subscription/current — the same route the
 * tier gates use) and caches a normalized tier snapshot locally so nav badges
 * and gates converge on the new entitlement.
 *
 * LP side: the F-195 webhook bridge inverts the order back to the tier id and
 * updates the tenant's subscription; buffy keeps that route authoritative.
 */
import { adminApi } from './api';
import { detectChurnTransition, trackChurn, trackGroup } from './analytics';

export const TIER_CACHE_KEY = 'lp_subscription_tier_cache';

export interface EntitlementSnapshot {
    tier: string | null;
    status: string | null;
    planName: string | null;
    fetchedAt: string;
}

/** Read the full last-known snapshot; null until a refresh happens this session. */
export function readCachedSnapshot(): EntitlementSnapshot | null {
    try {
        const raw = localStorage.getItem(TIER_CACHE_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as EntitlementSnapshot;
    } catch {
        return null;
    }
}

/** Human label for a tier id (badge text — T-173-002). */
export function getTierLabel(tier: string | null | undefined): string {
    const map: Record<string, string> = {
        freemium: 'Freemium',
        essentials: 'Essentials',
        professional: 'Professional',
        enterprise: 'Enterprise',
        pro: 'Professional',
        sandbox: 'Sandbox',
    };
    const key = tier == null ? '' : String(tier).trim().toLowerCase();
    return map[key] || (tier ? String(tier) : 'Freemium');
}

/** Read the last known tier (null until a refresh happens this session). */
export function readCachedTier(): string | null {
    try {
        const raw = localStorage.getItem(TIER_CACHE_KEY);
        if (!raw) return null;
        const snap = JSON.parse(raw) as EntitlementSnapshot;
        return snap.tier ?? null;
    } catch {
        return null;
    }
}

/**
 * Re-read the tenant's subscription from LP. Writes the normalized tier into
 * the tier cache so badges/gates pick it up immediately. Fail-closed: throws
 * on network/auth failure so callers can surface activation-pending state.
 */
export async function refreshSubscriptionEntitlement(): Promise<EntitlementSnapshot> {
    const prev = readCachedSnapshot();
    const res = await adminApi.get('api/subscription/current');
    const data = res?.data?.data ?? res?.data ?? {};
    const snapshot: EntitlementSnapshot = {
        tier: data.tier ?? data.plan_tier ?? null,
        status: data.status ?? null,
        planName: data.plan_name ?? data.planName ?? null,
        fetchedAt: new Date().toISOString(),
    };
    // Batch 4 S6 (churn) + S7 (plan gevents): only fire on real transitions so
    // the funnel events stay meaningful for the significance calculator.
    if (detectChurnTransition(prev, snapshot)) {
        trackChurn({ tier: snapshot.tier, status: snapshot.status });
    }
    if (prev && prev.tier && prev.tier !== snapshot.tier) {
        trackGroup({ plan: snapshot.tier, tier: snapshot.tier, status: snapshot.status });
    }
    try {
        localStorage.setItem(TIER_CACHE_KEY, JSON.stringify(snapshot));
    } catch {
        /* quota/private-mode — snapshot still returned to caller */
    }
    return snapshot;
}